import { useState } from 'react';
import Modal from '../Modal';
import { useNetwork, useSwitchNetwork } from 'wagmi';
import { CHAIN_ID, getNetworkName } from '../../config';
import classes from './NetworkSwitchModal.module.css';

interface NetworkSwitchModalProps {
  onDismiss: () => void;
}

const NetworkSwitchModal = ({ onDismiss }: NetworkSwitchModalProps) => {
  const { chain } = useNetwork();
  const { switchNetworkAsync, isLoading } = useSwitchNetwork();
  const [error, setError] = useState<string | null>(null);

  const currentNetworkName = getNetworkName(chain?.id);
  const targetNetworkName = getNetworkName(CHAIN_ID);

  const handleSwitchNetwork = async () => {
    setError(null);
    if (!switchNetworkAsync) {
      setError('Network switching not supported by your wallet');
      return;
    }

    try {
      await switchNetworkAsync(CHAIN_ID);
      onDismiss();
    } catch (err: unknown) {
      const error = err as { code?: number; message?: string };

      // Log for debugging (errors not visible to support teams otherwise)
      console.error('Network switch failed:', {
        targetChainId: CHAIN_ID,
        errorCode: error.code,
        errorMessage: error.message,
      });

      // EIP-1193 error code 4001 = user rejected request
      // Case-insensitive message check for wallets not using standard error codes
      const message = error.message?.toLowerCase() || '';
      const isUserRejection = error.code === 4001 ||
        ['user rejected', 'user denied', 'user cancelled'].some(p => message.includes(p));

      if (isUserRejection) {
        setError('You declined the request');
      } else {
        setError(error.message || 'Failed to switch network');
      }
    }
  };

  const content = (
    <div className={classes.networkSwitch}>
      <div className={classes.warningIcon}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <p className={classes.warningText}>Wrong Network</p>

      <p className={classes.infoText}>
        You're connected to <strong>{currentNetworkName}</strong>.
        <br />
        Please switch to <strong>{targetNetworkName}</strong> to continue.
      </p>

      {error && <p className={classes.errorText}>{error}</p>}

      <button
        className={classes.switchButton}
        onClick={handleSwitchNetwork}
        disabled={isLoading}
      >
        {isLoading ? (
          <span className={classes.loadingContent}>
            <span className={classes.spinner} />
            Switching...
          </span>
        ) : (
          `Switch to ${targetNetworkName}`
        )}
      </button>

      <button className={classes.dismissLink} onClick={onDismiss}>
        Continue anyway
      </button>
    </div>
  );

  return <Modal title="" content={content} onDismiss={onDismiss} />;
};

export default NetworkSwitchModal;
